"use client";

// src/components/tree-view/TreeItem.tsx
import { useDroppable as useDroppable2 } from "@dnd-kit/core";
import { cn as cn4 } from "@tessel/utils";
import React4 from "react";

// src/components/tree-view/styles.ts
import { cva } from "class-variance-authority";
var treeVariants = cva(
  "group relative hover:bg-hover rounded-lg px-2 transition-colors duration-150 cursor-pointer"
);
var selectedTreeVariants = cva(
  "bg-selection hover:bg-selection text-selection-foreground font-medium"
);
var dragOverVariants = cva(
  "before:absolute before:inset-0 before:rounded-lg before:bg-primary/10 before:border-2 before:border-primary before:border-dashed before:pointer-events-none"
);
var insertionLineVariants = cva(
  "absolute left-0 right-0 w-full transition-all duration-200 z-20 cursor-pointer flex items-center justify-center pointer-events-auto",
  {
    variants: {
      position: {
        before: "h-4 -top-2",
        // Positioned above the item, increased height
        after: "h-4 -bottom-2"
        // Positioned below the item, increased height
      },
      visible: {
        true: "opacity-100",
        // Remove background, keep only the indicator line
        false: "opacity-0"
        // Hidden but still in DOM for drag events
      }
    },
    defaultVariants: {
      visible: false
    }
  }
);
var insertionLineIndicatorVariants = cva(
  "w-full h-0.5 bg-primary rounded-full transition-all duration-200 shadow-sm"
);

// src/components/tree-view/TreeNode.tsx
import { useDraggable, useDroppable } from "@dnd-kit/core";
import * as AccordionPrimitive2 from "@radix-ui/react-accordion";
import { cn as cn3 } from "@tessel/utils";
import { Loader2 as Loader22 } from "lucide-react";
import React3 from "react";

// src/components/tree-view/AccordionComponents.tsx
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "@tessel/utils";
import { ChevronRight } from "lucide-react";
import React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var TreeAccordionTriggerComponent = React.forwardRef(
  ({
    className,
    children,
    onItemClick,
    itemIcon,
    isOpen,
    hasChildren,
    selectable,
    ...props
  }, ref) => {
    const ItemIcon = itemIcon;
    const triggerRef = React.useRef(null);
    const chevronContainerClassName = React.useMemo(() => {
      return cn(
        "absolute inset-0 flex items-center justify-center transition-opacity",
        ItemIcon ? "opacity-0 group-hover:opacity-100" : "opacity-100"
      );
    }, [ItemIcon]);
    const chevronClassName = React.useMemo(() => {
      return cn(
        "h-4 w-4 shrink-0 transition-transform duration-200",
        isOpen && "transform rotate-90"
      );
    }, [isOpen]);
    const contentClassName = React.useMemo(() => {
      return cn(
        "flex-1 flex items-center cursor-pointer px-2 py-1 ml-1 min-w-0",
        className
      );
    }, [className]);
    const handleContentClick = React.useCallback(() => {
      if (selectable === false) {
        triggerRef.current?.click();
      } else {
        onItemClick?.();
      }
    }, [selectable, onItemClick]);
    return /* @__PURE__ */ jsx(AccordionPrimitive.Header, { children: /* @__PURE__ */ jsxs("div", { className: "flex flex-1 w-full items-center py-1 relative group-hover:pr-16", children: [
      /* @__PURE__ */ jsxs(
        AccordionPrimitive.Trigger,
        {
          ref: (node) => {
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            triggerRef.current = node;
          },
          className: cn(
            "flex items-center justify-center w-6 h-6 hover:bg-accent/70 hover:text-accent-foreground rounded transition-all relative cursor-pointer"
          ),
          ...props,
          children: [
            ItemIcon && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center group-hover:hidden", children: /* @__PURE__ */ jsx(ItemIcon, { className: "h-4 w-4 shrink-0 text-muted-foreground/80" }) }),
            /* @__PURE__ */ jsx("div", { className: chevronContainerClassName, children: /* @__PURE__ */ jsx(ChevronRight, { className: chevronClassName }) })
          ]
        }
      ),
      /* @__PURE__ */ jsx("div", { className: contentClassName, onClick: handleContentClick, children })
    ] }) });
  }
);
var TreeAccordionTrigger = React.memo(
  TreeAccordionTriggerComponent,
  (prevProps, nextProps) => {
    const isOpenChanged = prevProps.isOpen !== nextProps.isOpen;
    const itemIconChanged = prevProps.itemIcon !== nextProps.itemIcon;
    const childrenChanged = prevProps.children !== nextProps.children;
    const hasChildrenChanged = prevProps.hasChildren !== nextProps.hasChildren;
    const classNameChanged = prevProps.className !== nextProps.className;
    const selectableChanged = prevProps.selectable !== nextProps.selectable;
    const onItemClickChanged = prevProps.onItemClick !== nextProps.onItemClick;
    const shouldUpdate = isOpenChanged || itemIconChanged || childrenChanged || hasChildrenChanged || classNameChanged || selectableChanged || onItemClickChanged;
    return !shouldUpdate;
  }
);
TreeAccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
var TreeAccordionContentComponent = React.forwardRef(({ className, children, ...props }, ref) => {
  const contentClassName = React.useMemo(() => {
    return cn(
      "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      className
    );
  }, [className]);
  return /* @__PURE__ */ jsx(
    AccordionPrimitive.Content,
    {
      ref,
      className: contentClassName,
      ...props,
      children: /* @__PURE__ */ jsx("div", { className: "pb-1 pt-0", children })
    }
  );
});
var TreeAccordionContent = React.memo(
  TreeAccordionContentComponent,
  (prevProps, nextProps) => {
    return prevProps.children === nextProps.children && prevProps.className === nextProps.className;
  }
);
TreeAccordionContent.displayName = AccordionPrimitive.Content.displayName;

// src/components/tree-view/TreeActions.tsx
import { cn as cn2 } from "@tessel/utils";
import { jsx as jsx2 } from "react/jsx-runtime";
var TreeActions = ({ children }) => {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: cn2(
        "absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1",
        "hidden group-hover:block has-[*[data-dropdown-open='true']]:block"
      ),
      children
    }
  );
};

// src/components/tree-view/utils.ts
import { Loader2 } from "lucide-react";
import React2 from "react";
var EMPTY_EXPANDED_ARRAY = [];
var itemNameCache = /* @__PURE__ */ new Map();
var renderItemName = (item) => {
  if (React2.isValidElement(item.name)) {
    return item.name;
  }
  const nameContent = typeof item.name === "string" ? item.name : String(item.name || "");
  const cacheKey = `${nameContent}:${item.isLoading ? "loading" : "idle"}`;
  const cached = itemNameCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const element = React2.createElement(
    "div",
    { className: "flex items-center gap-2" },
    React2.createElement("span", null, nameContent),
    item.isLoading && React2.createElement(Loader2, { className: "h-3 w-3 animate-spin" })
  );
  itemNameCache.set(cacheKey, element);
  return element;
};
var findItemById = (id, items) => {
  const itemArray = Array.isArray(items) ? items : [items];
  for (const item of itemArray) {
    if (item.id === id) {
      return item;
    }
    if (item.children) {
      const found = findItemById(id, item.children);
      if (found) {
        return found;
      }
    }
  }
  return null;
};
var isDescendantOf = (itemId, potentialAncestor) => {
  if (!potentialAncestor) {
    return false;
  }
  if (potentialAncestor.id === itemId) {
    return true;
  }
  if (potentialAncestor.children && potentialAncestor.children.length > 0) {
    for (const child of potentialAncestor.children) {
      if (isDescendantOf(itemId, child)) {
        return true;
      }
    }
  }
  return false;
};
var computeExpandedItemIds = (data, expandAll, initialSelectedItemId, expandedItemIds) => {
  if (expandedItemIds) {
    return expandedItemIds;
  }
  if (!initialSelectedItemId) {
    return EMPTY_EXPANDED_ARRAY;
  }
  const ids = [];
  function walkTreeItems(items, targetId) {
    if (Array.isArray(items)) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item) {
          ids.push(item.id);
          if (walkTreeItems(item, targetId) && !expandAll) {
            return true;
          }
          if (!expandAll) {
            ids.pop();
          }
        }
      }
    } else if (!expandAll && items.id === targetId) {
      return true;
    } else if (items.children) {
      return walkTreeItems(items.children, targetId);
    }
  }
  walkTreeItems(data, initialSelectedItemId);
  return ids;
};

// src/components/tree-view/TreeNode.tsx
import { Fragment, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var TreeItemComponentLazy = React3.lazy(
  () => import("./TreeItem-RQHHOXHD.js").then((module) => ({
    default: module.TreeItemComponent
  }))
);
var MemoizedTreeChildren = React3.memo(
  ({
    children,
    selectedItemId,
    handleSelectChange,
    expandedItemIds,
    onExpandedIdsChange,
    defaultLeafIcon,
    defaultNodeIcon,
    draggedItem,
    insertionTarget,
    setInsertionTarget,
    emptyStateMessage,
    loadingText
  }) => {
    return /* @__PURE__ */ jsx3(
      React3.Suspense,
      {
        fallback: /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2 py-2 px-2 text-muted-foreground", children: [
          /* @__PURE__ */ jsx3(Loader22, { className: "h-4 w-4 animate-spin" }),
          loadingText && /* @__PURE__ */ jsx3("span", { className: "text-sm", children: loadingText })
        ] }),
        children: /* @__PURE__ */ jsx3(
          TreeItemComponentLazy,
          {
            data: children,
            selectedItemId,
            handleSelectChange,
            expandedItemIds,
            onExpandedIdsChange,
            defaultLeafIcon,
            defaultNodeIcon,
            draggedItem,
            insertionTarget,
            setInsertionTarget,
            emptyStateMessage
          }
        )
      }
    );
  },
  (prevProps, nextProps) => {
    const dragStarted = !prevProps.draggedItem && nextProps.draggedItem;
    const dragEnded = prevProps.draggedItem && !nextProps.draggedItem;
    if (dragStarted || dragEnded) {
      return false;
    }
    if (prevProps.children !== nextProps.children) {
      return false;
    }
    const relevantSelectedChange = prevProps.selectedItemId !== nextProps.selectedItemId && (prevProps.children.some(
      (child) => child.id === prevProps.selectedItemId
    ) || nextProps.children.some(
      (child) => child.id === nextProps.selectedItemId
    ));
    if (relevantSelectedChange) {
      return false;
    }
    const relevantExpansionChange = prevProps.expandedItemIds !== nextProps.expandedItemIds && prevProps.children.some(
      (child) => prevProps.expandedItemIds?.includes(child.id) !== nextProps.expandedItemIds?.includes(child.id)
    );
    if (relevantExpansionChange) {
      return false;
    }
    const relevantDragChange = prevProps.draggedItem !== nextProps.draggedItem && prevProps.children.some(
      (child) => child.id === prevProps.draggedItem?.id || child.id === nextProps.draggedItem?.id
    );
    if (relevantDragChange) {
      return false;
    }
    if (prevProps.insertionTarget !== nextProps.insertionTarget) {
      return false;
    }
    if (prevProps.handleSelectChange !== nextProps.handleSelectChange) {
      return false;
    }
    if (prevProps.onExpandedIdsChange !== nextProps.onExpandedIdsChange) {
      return false;
    }
    if (prevProps.setInsertionTarget !== nextProps.setInsertionTarget) {
      return false;
    }
    if (prevProps.defaultLeafIcon !== nextProps.defaultLeafIcon) {
      return false;
    }
    if (prevProps.defaultNodeIcon !== nextProps.defaultNodeIcon) {
      return false;
    }
    if (prevProps.emptyStateMessage !== nextProps.emptyStateMessage) {
      return false;
    }
    return true;
  }
);
var TreeNodeComponent = ({
  item,
  handleSelectChange,
  expandedItemIds,
  onExpandedIdsChange,
  selectedItemId,
  defaultNodeIcon,
  defaultLeafIcon,
  draggedItem,
  insertionTarget,
  setInsertionTarget,
  emptyStateMessage,
  loadingText
}) => {
  const [isMounted, setIsMounted] = React3.useState(false);
  React3.useEffect(() => {
    setIsMounted(true);
  }, []);
  const [value, setValue] = React3.useState(
    () => expandedItemIds?.includes(item.id) ? [item.id] : []
  );
  const [_isDragOver, _setIsDragOver] = React3.useState(false);
  const handleItemClick = React3.useCallback(() => {
    if (item.selectable !== false) {
      const isCurrentlySelected = selectedItemId === item.id;
      if (!isCurrentlySelected) {
        handleSelectChange(item);
      }
    }
    if (item.onClick) {
      item.onClick();
    }
  }, [handleSelectChange, item, selectedItemId]);
  const memoizedChildren = React3.useMemo(() => {
    if (item.isLoading) {
      return /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2 py-2 px-2 text-muted-foreground", children: [
        /* @__PURE__ */ jsx3(Loader22, { className: "h-4 w-4 animate-spin" }),
        loadingText && /* @__PURE__ */ jsx3("span", { className: "text-sm", children: loadingText })
      ] });
    }
    if (!item.children || item.children.length === 0) {
      return /* @__PURE__ */ jsx3("div", { className: "py-2 px-2 text-sm text-muted-foreground italic", children: emptyStateMessage });
    }
    return /* @__PURE__ */ jsx3(
      MemoizedTreeChildren,
      {
        children: item.children,
        selectedItemId,
        handleSelectChange,
        expandedItemIds,
        onExpandedIdsChange,
        defaultLeafIcon,
        defaultNodeIcon,
        draggedItem,
        insertionTarget,
        setInsertionTarget,
        emptyStateMessage,
        loadingText
      }
    );
  }, [
    item.children,
    item.isLoading,
    emptyStateMessage,
    loadingText,
    selectedItemId,
    handleSelectChange,
    expandedItemIds,
    onExpandedIdsChange,
    defaultLeafIcon,
    defaultNodeIcon,
    draggedItem,
    insertionTarget,
    setInsertionTarget
  ]);
  const {
    attributes: draggableAttributes,
    listeners: draggableListeners,
    setNodeRef: setDraggableRef,
    isDragging
  } = useDraggable({
    id: item.id,
    disabled: !item.draggable,
    data: {
      type: "tree-item",
      item
    }
  });
  const isDescendantOfDragged = isDescendantOf(item.id, draggedItem);
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id: item.id,
    disabled: item.droppable === false || isDescendantOfDragged,
    data: {
      type: "tree-item",
      item
    }
  });
  const setNodeRef = React3.useCallback(
    (node) => {
      setDraggableRef(node);
      setDroppableRef(node);
    },
    [setDraggableRef, setDroppableRef]
  );
  const dragStyle = void 0;
  const memoizedItemName = React3.useMemo(() => {
    return renderItemName(item);
  }, [item]);
  const memoizedTriggerChildren = React3.useMemo(() => {
    const itemName = /* @__PURE__ */ jsx3("div", { className: "flex-1 flex items-center min-w-0", children: /* @__PURE__ */ jsx3("div", { className: "text-sm truncate flex-1 min-w-0", children: memoizedItemName }) });
    const actions = item.actions ? /* @__PURE__ */ jsx3(TreeActions, { children: item.actions }) : null;
    return /* @__PURE__ */ jsxs2(Fragment, { children: [
      itemName,
      actions
    ] });
  }, [memoizedItemName, item.actions]);
  const prevExpandedIds = React3.useRef(expandedItemIds);
  const currentValueRef = React3.useRef(value);
  currentValueRef.current = value;
  React3.useEffect(() => {
    const shouldBeExpanded = expandedItemIds?.includes(item.id);
    const isCurrentlyExpanded = currentValueRef.current.includes(item.id);
    const wasInPreviousIds = prevExpandedIds.current?.includes(item.id);
    if (shouldBeExpanded && !isCurrentlyExpanded && !wasInPreviousIds) {
      setValue([item.id]);
    } else if (!shouldBeExpanded && isCurrentlyExpanded && wasInPreviousIds) {
      setValue([]);
    }
    prevExpandedIds.current = expandedItemIds || [];
  }, [expandedItemIds, item.id]);
  const hasChildren = React3.useMemo(() => {
    return Boolean(
      item.children && item.children.length > 0 || item.hasContent
    );
  }, [item.children, item.hasContent]);
  const isOpen = React3.useMemo(() => {
    return value.includes(item.id);
  }, [value, item.id]);
  const itemIcon = React3.useMemo(() => {
    return item.icon || defaultNodeIcon;
  }, [item.icon, defaultNodeIcon]);
  const containerClassName = React3.useMemo(() => {
    const isSelected = selectedItemId === item.id;
    return cn3(
      treeVariants(),
      isSelected && selectedTreeVariants(),
      isOver && dragOverVariants(),
      isDragging && "opacity-50",
      // Show dragged item with reduced opacity
      "group"
      // Make each item its own group
    );
  }, [selectedItemId, item.id, isOver, isDragging]);
  const expandedItemIdsRef = React3.useRef(expandedItemIds);
  expandedItemIdsRef.current = expandedItemIds;
  const handleValueChange = React3.useCallback(
    (s) => {
      setValue(s);
      if (onExpandedIdsChange) {
        const currentExpanded = expandedItemIdsRef.current || [];
        const newExpanded = s.includes(item.id) ? (
          // Se o item está sendo expandido, adiciona à lista (se não estiver já)
          currentExpanded.includes(item.id) ? currentExpanded : [...currentExpanded, item.id]
        ) : (
          // Se o item está sendo recolhido, remove da lista
          currentExpanded.filter((id) => id !== item.id)
        );
        onExpandedIdsChange(newExpanded);
      }
    },
    [item.id, onExpandedIdsChange]
  );
  const memoizedDraggableProps = React3.useMemo(
    () => isMounted ? {
      ...draggableAttributes,
      ...draggableListeners
    } : {},
    [isMounted, draggableAttributes, draggableListeners]
  );
  const treeContent = /* @__PURE__ */ jsx3("div", { className: "relative", children: /* @__PURE__ */ jsx3(
    AccordionPrimitive2.Root,
    {
      type: "multiple",
      value,
      onValueChange: handleValueChange,
      children: /* @__PURE__ */ jsxs2(AccordionPrimitive2.Item, { value: item.id, children: [
        /* @__PURE__ */ jsx3(
          "div",
          {
            ref: setNodeRef,
            style: dragStyle,
            className: containerClassName,
            ...memoizedDraggableProps,
            children: /* @__PURE__ */ jsx3(
              TreeAccordionTrigger,
              {
                onItemClick: handleItemClick,
                itemIcon,
                isOpen,
                hasChildren,
                selectable: item.selectable,
                children: memoizedTriggerChildren
              }
            )
          }
        ),
        /* @__PURE__ */ jsx3(TreeAccordionContent, { className: "ml-4 pl-1 border-l", children: memoizedChildren })
      ] })
    }
  ) });
  if (item.renderWrapper) {
    return item.renderWrapper(treeContent);
  }
  return treeContent;
};
var TreeNode = React3.memo(
  TreeNodeComponent,
  (prevProps, nextProps) => {
    const dragStarted = !prevProps.draggedItem && nextProps.draggedItem;
    const dragEnded = prevProps.draggedItem && !nextProps.draggedItem;
    if (dragStarted || dragEnded) {
      return false;
    }
    if (prevProps.item === nextProps.item) {
      return prevProps.selectedItemId === nextProps.selectedItemId && prevProps.expandedItemIds === nextProps.expandedItemIds && prevProps.draggedItem === nextProps.draggedItem && prevProps.insertionTarget === nextProps.insertionTarget && prevProps.handleSelectChange === nextProps.handleSelectChange && prevProps.onExpandedIdsChange === nextProps.onExpandedIdsChange && prevProps.setInsertionTarget === nextProps.setInsertionTarget;
    }
    const itemContentChanged = prevProps.item.id !== nextProps.item.id || prevProps.item.name !== nextProps.item.name || prevProps.item.children !== nextProps.item.children || prevProps.item.actions !== nextProps.item.actions || prevProps.item.hasContent !== nextProps.item.hasContent;
    if (itemContentChanged) {
      return false;
    }
    const thisItemId = prevProps.item.id;
    const wasSelected = prevProps.selectedItemId === thisItemId;
    const isSelected = nextProps.selectedItemId === thisItemId;
    if (wasSelected !== isSelected) {
      return false;
    }
    const wasExpanded = prevProps.expandedItemIds?.includes(thisItemId);
    const isExpanded = nextProps.expandedItemIds?.includes(thisItemId);
    if (wasExpanded !== isExpanded) {
      return false;
    }
    const wasDragged = prevProps.draggedItem?.id === thisItemId;
    const isDragged = nextProps.draggedItem?.id === thisItemId;
    if (wasDragged !== isDragged) {
      return false;
    }
    const wasInsertionTarget = prevProps.insertionTarget?.itemId === thisItemId;
    const isInsertionTarget = nextProps.insertionTarget?.itemId === thisItemId;
    if (wasInsertionTarget !== isInsertionTarget) {
      return false;
    }
    if (prevProps.handleSelectChange !== nextProps.handleSelectChange || prevProps.onExpandedIdsChange !== nextProps.onExpandedIdsChange || prevProps.setInsertionTarget !== nextProps.setInsertionTarget) {
      return false;
    }
    return true;
  }
);
TreeNode.displayName = "TreeNode";
MemoizedTreeChildren.displayName = "MemoizedTreeChildren";

// src/components/tree-view/TreeItem.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var InsertionLine = ({
  position,
  referenceItemId,
  currentInsertionTarget,
  disabled
}) => {
  const insertionId = `${referenceItemId}-${position}`;
  const isVisible = Boolean(
    currentInsertionTarget && currentInsertionTarget.itemId === referenceItemId && currentInsertionTarget.position === position
  );
  const { setNodeRef } = useDroppable2({
    id: insertionId,
    disabled,
    data: {
      type: "insertion-zone",
      itemId: referenceItemId,
      position
    }
  });
  return /* @__PURE__ */ jsx4(
    "div",
    {
      ref: setNodeRef,
      className: cn4(
        insertionLineVariants({
          position,
          visible: isVisible && !disabled
        })
      ),
      children: /* @__PURE__ */ jsx4("div", { className: cn4(insertionLineIndicatorVariants()) })
    }
  );
};
var TreeNodeWrapper = React4.memo(
  ({
    item,
    selectedItemId,
    expandedItemIds,
    onExpandedIdsChange,
    handleSelectChange,
    defaultNodeIcon,
    defaultLeafIcon,
    draggedItem,
    insertionTarget,
    setInsertionTarget,
    emptyStateMessage,
    loadingText
  }) => {
    const treeNodeProps = {
      item,
      onExpandedIdsChange,
      handleSelectChange,
      defaultNodeIcon,
      defaultLeafIcon,
      setInsertionTarget,
      emptyStateMessage,
      loadingText,
      // CRITICAL: Always pass selectedItemId so TreeNode can apply correct styles
      selectedItemId
    };
    treeNodeProps.expandedItemIds = expandedItemIds;
    const isThisItemDragged = draggedItem?.id === item.id;
    if (isThisItemDragged) {
      treeNodeProps.draggedItem = draggedItem;
    }
    treeNodeProps.insertionTarget = insertionTarget;
    return /* @__PURE__ */ jsx4(TreeNode, { ...treeNodeProps });
  },
  (prevProps, nextProps) => {
    if (prevProps.item !== nextProps.item) {
      return false;
    }
    const wasSelected = prevProps.selectedItemId === prevProps.item.id;
    const isSelected = nextProps.selectedItemId === nextProps.item.id;
    if (wasSelected !== isSelected) {
      return false;
    }
    const wasExpanded = prevProps.expandedItemIds?.includes(prevProps.item.id);
    const isExpanded = nextProps.expandedItemIds?.includes(nextProps.item.id);
    if (wasExpanded !== isExpanded) {
      return false;
    }
    if (prevProps.insertionTarget !== nextProps.insertionTarget) {
      return false;
    }
    const wasDragged = prevProps.draggedItem?.id === prevProps.item.id;
    const isDragged = nextProps.draggedItem?.id === nextProps.item.id;
    if (wasDragged !== isDragged) {
      return false;
    }
    if (prevProps.handleSelectChange !== nextProps.handleSelectChange || prevProps.onExpandedIdsChange !== nextProps.onExpandedIdsChange || prevProps.setInsertionTarget !== nextProps.setInsertionTarget) {
      return false;
    }
    return true;
  }
);
var TreeItemComponent = React4.forwardRef(
  ({
    className,
    data,
    selectedItemId,
    handleSelectChange,
    expandedItemIds,
    onExpandedIdsChange,
    defaultNodeIcon,
    defaultLeafIcon,
    draggedItem,
    insertionTarget,
    setInsertionTarget,
    emptyStateMessage,
    loadingText,
    ...props
  }, ref) => {
    if (!Array.isArray(data)) {
      data = [data];
    }
    const createInsertionLine = (position, referenceItemId) => {
      const simpleKey = `${referenceItemId}-${position}`;
      const isDisabled = isDescendantOf(referenceItemId, draggedItem);
      return /* @__PURE__ */ jsx4(
        InsertionLine,
        {
          position,
          referenceItemId,
          currentInsertionTarget: insertionTarget,
          disabled: isDisabled
        },
        simpleKey
      );
    };
    return /* @__PURE__ */ jsx4("div", { ref, role: "tree", className, ...props, children: /* @__PURE__ */ jsx4("ul", { children: data.map((item, index) => /* @__PURE__ */ jsxs3("li", { className: "relative", children: [
      index === 0 && createInsertionLine("before", item.id),
      createInsertionLine("after", item.id),
      /* @__PURE__ */ jsx4(
        TreeNodeWrapper,
        {
          item,
          selectedItemId,
          expandedItemIds,
          onExpandedIdsChange,
          handleSelectChange,
          defaultNodeIcon,
          defaultLeafIcon,
          draggedItem,
          insertionTarget,
          setInsertionTarget,
          emptyStateMessage,
          loadingText
        },
        item.id
      )
    ] }, item.id)) }) });
  }
);
TreeItemComponent.displayName = "TreeItem";
var TreeItem = TreeItemComponent;

export {
  findItemById,
  computeExpandedItemIds,
  TreeItemComponent,
  TreeItem
};
//# sourceMappingURL=chunk-YNZ7LDHP.js.map